import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { Modal, PageHeader, EmptyState, PAGE_SIZE, SearchSelect } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Helpers ──────────────────────────────────────────────────────────────────
// solution field stores: "維修方式：xxx\n使用備品：yyy" for backward-compat
const parseSolution = (text = '') => {
  if (!text) return { solution: '', parts: '' }
  const lines = text.split('\n')
  const hasTags = lines.some(l => l.startsWith('維修方式：') || l.startsWith('使用備品：'))
  if (!hasTags) return { solution: text, parts: '' }
  const sol   = lines.filter(l => l.startsWith('維修方式：')).map(l => l.slice(5)).join('\n')
  const parts = lines.filter(l => l.startsWith('使用備品：')).map(l => l.slice(5)).join('\n')
  return { solution: sol, parts }
}
const buildSolution = (solution, parts) =>
  [solution && `維修方式：${solution}`, parts && `使用備品：${parts}`].filter(Boolean).join('\n')

const cardBorderColor = (r, statusInfo) => {
  if (r.is_stopped && r.status !== 'completed') return '#ef4444'
  return statusInfo(r.status).color
}

// sort: stopped active first → pending → in_progress → waiting_parts → completed
const sortRecords = records => [
  ...records.filter(r => r.is_stopped && r.status !== 'completed'),
  ...records.filter(r => !r.is_stopped && r.status === 'pending'),
  ...records.filter(r => !r.is_stopped && r.status === 'in_progress'),
  ...records.filter(r => r.status === 'waiting_parts'),
  ...records.filter(r => r.status === 'completed'),
]

// ── Empty forms ───────────────────────────────────────────────────────────────
const emptyForm = () => ({
  machine_name: '', machine_code: '',
  report_date: new Date().toISOString().slice(0, 10),
  type: 'breakdown', status: 'pending',
  is_stopped: false, reported: false,
  reporter: '', assignee: '', machine_responsible: '',
  description: '', solution: '', parts_used: '', cost: '', estimated_done: '',
})

const emptyMachineForm = () => ({
  name: '', code: '', department: '', location: '', responsible: '', supplier: '', status: 'active', note: '',
})

// ── Sub-components ────────────────────────────────────────────────────────────

// Inline badge
function Badge({ type, label, bg, color }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: bg, color, border: `1px solid ${color}44`, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  )
}

// Maintenance record card
function RecordCard({ r, canManage, canAssign, onDetail, onAction, onDelete, typeInfo, statusInfo, _ }) {
  const s = statusInfo(r.status)
  const isActive = r.status !== 'completed'
  const isStopped = r.is_stopped && isActive
  const { solution, parts } = parseSolution(r.solution)

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      border: `1.5px solid ${isStopped ? '#ef4444' : C.border}`,
      borderLeft: `5px solid ${cardBorderColor(r, statusInfo)}`,
      padding: '14px 16px',
      opacity: r.status === 'completed' ? 0.8 : 1,
      transition: 'box-shadow 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}
    >
      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 900, color: C.text }}>{r.machine_name || r.machine_code}</span>
            {r.machine_code && r.machine_name && (
              <span style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 7px', borderRadius: 8 }}>{r.machine_code}</span>
            )}
            {isStopped && (
              <span style={{ fontSize: 11, fontWeight: 800, color: '#ef4444', background: '#fee2e2', padding: '2px 8px', borderRadius: 10, border: '1px solid #fca5a5', animation: 'none' }}>
                🔴 停機
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
            📅 {formatDate(r.report_date)}
            {r.estimated_done && <span style={{ marginLeft: 10 }}>🎯 預計完成：{r.estimated_done}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Badge label={`${typeInfo(r.type).icon} ${typeInfo(r.type).label}`} color={typeInfo(r.type).color} bg={typeInfo(r.type).bg} />
          <Badge label={`${s.icon} ${s.label}`} color={s.color} bg={s.bg} />
        </div>
      </div>

      {/* Reporter / assignee row */}
      <div style={{ fontSize: 12, color: C.muted, marginBottom: r.description ? 8 : 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span>👤 通報：<strong style={{ color: C.text }}>{r.reporter || '-'}</strong></span>
        {r.assignee && <span>🔧 維修：<strong style={{ color: C.text }}>{r.assignee}</strong></span>}
        {r.machine_responsible && <span>📌 負責：{r.machine_responsible}</span>}
        {r.reported && <span style={{ color: '#0891b2', fontWeight: 600 }}>📢 已通報主管</span>}
        {r.cost > 0 && <span style={{ color: '#7c3aed', fontWeight: 600 }}>💰 NT${Number(r.cost).toLocaleString()}</span>}
      </div>

      {/* Problem description */}
      {r.description && (
        <div style={{ fontSize: 12, color: C.text, background: '#fafafa', borderRadius: 8, padding: '7px 10px', marginBottom: 8, borderLeft: `3px solid ${typeInfo(r.type).color}44`, lineHeight: 1.5 }}>
          {r.description.length > 100 ? r.description.slice(0, 100) + '…' : r.description}
        </div>
      )}

      {/* Parts used (if any) */}
      {parts && (
        <div style={{ fontSize: 11, color: '#f59e0b', background: '#fffbeb', borderRadius: 8, padding: '5px 10px', marginBottom: 8, border: '1px solid #fde68a' }}>
          🔩 使用備品：{parts}
        </div>
      )}

      {/* Solution summary (completed only) */}
      {r.status === 'completed' && solution && (
        <div style={{ fontSize: 11, color: '#10b981', background: '#d1fae5', borderRadius: 8, padding: '5px 10px', marginBottom: 8, border: '1px solid #a7f3d0' }}>
          ✅ {solution.length > 80 ? solution.slice(0, 80) + '…' : solution}
        </div>
      )}

      {/* Action buttons — 循序漸進 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' }}>
        {/* Step actions by status */}
        {r.status === 'pending' && canAssign && (
          <button style={{ ...st.btn('primary'), padding: '7px 14px', fontSize: 12 }} onClick={() => onAction('assign', r)}>
            {_('mntBtnAssign')}
          </button>
        )}
        {r.status === 'in_progress' && (
          <>
            <button style={{ ...st.btn('default'), padding: '7px 12px', fontSize: 12 }} onClick={() => onAction('waitparts', r)}>
              {_('mntBtnWaitParts')}
            </button>
            <button style={{ ...st.btn('success'), padding: '7px 14px', fontSize: 12 }} onClick={() => onAction('complete', r)}>
              {_('mntBtnComplete')}
            </button>
          </>
        )}
        {r.status === 'waiting_parts' && (
          <button style={{ ...st.btn('success'), padding: '7px 14px', fontSize: 12 }} onClick={() => onAction('complete', r)}>
            {_('mntBtnComplete')}
          </button>
        )}

        <button style={{ ...st.btn('default'), padding: '7px 12px', fontSize: 12 }} onClick={() => onDetail(r)}>{_('mntBtnDetail')}</button>
        {canManage && (
          <>
            <button style={{ ...st.btn('warning'), padding: '7px 10px', fontSize: 12 }} onClick={() => onAction('edit', r)}>{_('mntBtnModify')}</button>
            <button style={{ ...st.btn('danger'), padding: '7px 10px', fontSize: 12, marginLeft: 'auto' }} onClick={() => onDelete(r.id, r.machine_name)}>刪除</button>
          </>
        )}
      </div>
    </div>
  )
}

// Machine management body
function MachineFormBody({ form, setForm }) {
  const f = (field, val) => setForm(p => ({ ...p, [field]: val }))
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
      {[['機台名稱 *', 'name', '例：CNC車床A'],['機台代號 *', 'code', '例：CNC-001'],['所屬部門', 'department', '例：生產部'],['位置', 'location', '例：一廠二線'],['負責人', 'responsible', '員工姓名'],['供應廠商', 'supplier', '廠商名稱 / 聯絡方式']].map(([label, key, ph]) => (
        <div key={key}>
          <label style={st.label}>{label}</label>
          <input style={st.input} value={form[key] || ''} placeholder={ph} onChange={e => f(key, e.target.value)} />
        </div>
      ))}
      <div>
        <label style={st.label}>狀態</label>
        <select style={st.input} value={form.status || 'active'} onChange={e => f('status', e.target.value)}>
          <option value="active">✅ 正常運作</option>
          <option value="maintenance">⚙️ 維修中</option>
          <option value="inactive">⛔ 停用</option>
        </select>
      </div>
      <div>
        <label style={st.label}>備注</label>
        <input style={st.input} value={form.note || ''} placeholder="其他說明" onChange={e => f('note', e.target.value)} />
      </div>
    </div>
  )
}

// ── Main Module ───────────────────────────────────────────────────────────────
export default function Maintenance({ userProfile, showToast, addLog, enabledModules = [], isMobile = false }) {
  const isAdmin   = userProfile?.role === 'admin'
  const pos       = userProfile?.position || 'user'
  const hasMaintenancePerm = enabledModules.includes('maintenance')
  const canManage = isAdmin || hasMaintenancePerm || ['factory_manager', 'senior_manager', 'section_chief', 'team_leader'].includes(pos)
  const canAssign = canManage || enabledModules.includes('assign')

  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  const TYPE_OPTIONS = [
    { key: 'breakdown',   label: _('mntTypeBreakdown'),  icon: '🔴', color: '#ef4444', bg: '#fee2e2' },
    { key: 'preventive',  label: _('mntTypePreventive'), icon: '🔵', color: '#0891b2', bg: '#e0f2fe' },
    { key: 'scheduled',   label: _('mntTypeScheduled'),  icon: '🟣', color: '#7c3aed', bg: '#ede9fe' },
    { key: 'inspection',  label: _('mntTypeInspection'), icon: '🟢', color: '#10b981', bg: '#d1fae5' },
    { key: 'parts',       label: _('mntTypeParts'),      icon: '🔩', color: '#f59e0b', bg: '#fef3c7' },
  ]

  const STATUS_OPTIONS = [
    { key: 'pending',       label: _('mntStatusPending'),    icon: '⏳', color: '#f59e0b', bg: '#fffbeb' },
    { key: 'in_progress',   label: _('mntStatusInProgress'), icon: '⚙️', color: '#3b82f6', bg: '#eff6ff' },
    { key: 'waiting_parts', label: _('mntStatusWaitParts'),  icon: '📦', color: '#7c3aed', bg: '#ede9fe' },
    { key: 'completed',     label: _('mntStatusCompleted'),  icon: '✅', color: '#10b981', bg: '#d1fae5' },
  ]

  const typeInfo   = k => TYPE_OPTIONS.find(t => t.key === k)   || TYPE_OPTIONS[0]
  const statusInfo = k => STATUS_OPTIONS.find(s => s.key === k) || STATUS_OPTIONS[0]

  const [records,   setRecords]   = useState([])
  const [machines,  setMachines]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [saving,    setSaving]    = useState(false)
  const [activeTab, setActiveTab] = useState('records')

  // actionModal: null | { type: 'report'|'assign'|'waitparts'|'complete'|'edit', record: null|obj }
  const [actionModal,     setActionModal]     = useState(null)
  const [form,            setForm]            = useState(emptyForm())
  const [detail,          setDetail]          = useState(null)

  const [machineForm,     setMachineForm]     = useState(emptyMachineForm())
  const [editingMachine,  setEditingMachine]  = useState(null)
  const [showAddMachine,  setShowAddMachine]  = useState(false)

  const [filterStatus, setFilterStatus] = useState('active') // 'active' | 'all' | specific status key
  const [filterType,   setFilterType]   = useState('all')
  const [search,       setSearch]       = useState('')
  const [page,         setPage]         = useState(1)

  const load = async () => {
    setLoading(true)
    const cid = userProfile?.company_id
    let recQ = supabase.from('maintenance').select('*').order('created_at', { ascending: false }).limit(500)
    if (cid) recQ = recQ.eq('company_id', cid)
    let machQ = supabase.from('machines').select('*').order('name').limit(200)
    if (cid) machQ = machQ.eq('company_id', cid)
    const [{ data: recs }, { data: machs }] = await Promise.all([recQ, machQ])
    if (recs)  setRecords(recs)
    if (machs) setMachines(machs)
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // Auto-fill reporter
  useEffect(() => {
    if (userProfile?.name) setForm(f => ({ ...f, reporter: f.reporter || userProfile.name }))
  }, [userProfile])

  const machineOptions = useMemo(() =>
    machines.map(m => ({ value: m.id, label: `${m.code} · ${m.name}`, sub: `${m.department || ''} ${m.location || ''}`.trim() }))
  , [machines])

  const handleMachineSelect = id => {
    const m = machines.find(m => m.id === id)
    if (m) setForm(f => ({ ...f, machine_name: f.machine_name || m.name, machine_code: m.code, machine_responsible: m.responsible || '' }))
  }

  // ── Stats ──
  const stats = useMemo(() => ({
    stopped:    records.filter(r => r.is_stopped && r.status !== 'completed').length,
    pending:    records.filter(r => r.status === 'pending').length,
    inProgress: records.filter(r => r.status === 'in_progress').length,
    waitParts:  records.filter(r => r.status === 'waiting_parts').length,
    completed:  records.filter(r => r.status === 'completed').length,
  }), [records])

  // ── Filter ──
  const filtered = useMemo(() => {
    let base = records
    if (filterStatus === 'active') base = base.filter(r => r.status !== 'completed')
    else if (filterStatus !== 'all') base = base.filter(r => r.status === filterStatus)
    if (filterType !== 'all') base = base.filter(r => r.type === filterType)
    if (search) {
      const q = search.toLowerCase()
      base = base.filter(r =>
        r.machine_name?.toLowerCase().includes(q) ||
        r.machine_code?.toLowerCase().includes(q) ||
        r.reporter?.includes(q) ||
        r.assignee?.includes(q) ||
        r.description?.includes(q)
      )
    }
    return sortRecords(base)
  }, [records, filterStatus, filterType, search])

  const paged      = useMemo(() => filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filtered, page])
  const totalPages = useMemo(() => Math.ceil(filtered.length / PAGE_SIZE), [filtered])

  // ── Open action modal ──
  const openAction = (type, record = null) => {
    if (type === 'report') {
      setForm({ ...emptyForm(), reporter: userProfile?.name || '' })
    } else if (record) {
      const { solution, parts } = parseSolution(record.solution)
      setForm({ ...record, solution, parts_used: parts, cost: record.cost || '', estimated_done: record.estimated_done || '' })
    }
    setActionModal({ type, record })
  }

  // ── Save new report ──
  const handleSaveReport = async () => {
    if (!form.machine_name && !form.machine_code) { showToast('請選擇或填寫機台', 'error'); return }
    if (!form.reporter) { showToast('請填寫通報人', 'error'); return }
    if (!form.description) { showToast('請填寫問題描述', 'error'); return }
    setSaving(true)
    const { id, created_at, parts_used, ...rest } = form
    const { error } = await supabase.from('maintenance').insert({
      ...rest, solution: '', cost: 0,
      report_date: form.report_date || null,
      updated_at: new Date().toISOString(),
      company_id: userProfile?.company_id || null,
    })
    if (error) { showToast('新增失敗：' + error.message, 'error'); setSaving(false); return }
    await addLog('新增維修通報', `機台 ${form.machine_name || form.machine_code} 報修`)
    showToast('✓ 維修通報已送出')
    setSaving(false); setActionModal(null); load()
  }

  // ── Focused action saves ──
  const handleActionSave = async (type) => {
    const rec = actionModal?.record
    setSaving(true)
    try {
      if (type === 'assign') {
        if (!form.assignee?.trim()) { showToast('請填寫維修負責人', 'error'); setSaving(false); return }
        await supabase.from('maintenance').update({
          assignee: form.assignee.trim(),
          estimated_done: form.estimated_done || null,
          status: 'in_progress',
          updated_at: new Date().toISOString(),
        }).eq('id', rec.id)
        await addLog('指派維修', `${rec.machine_name} 指派給 ${form.assignee}`)
        showToast(`✓ 已指派給 ${form.assignee}，開始維修`)

      } else if (type === 'waitparts') {
        const { solution: existSol } = parseSolution(rec.solution)
        await supabase.from('maintenance').update({
          solution: buildSolution(existSol, form.parts_used),
          status: 'waiting_parts',
          updated_at: new Date().toISOString(),
        }).eq('id', rec.id)
        await addLog('更新維修', `${rec.machine_name} → 等待零件`)
        showToast('✓ 已更新為：等待零件')

      } else if (type === 'complete') {
        if (!form.solution?.trim()) { showToast('請填寫維修方式', 'error'); setSaving(false); return }
        await supabase.from('maintenance').update({
          solution: buildSolution(form.solution.trim(), form.parts_used),
          cost: parseFloat(form.cost) || 0,
          status: 'completed', completed: true,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }).eq('id', rec.id)
        await addLog('完成維修', `${rec.machine_name} 維修完工`)
        showToast('✅ 維修完工！')

      } else if (type === 'edit') {
        // Full edit for canManage
        const { id, created_at, parts_used, ...rest } = form
        const payload = {
          ...rest,
          solution: buildSolution(form.solution, form.parts_used),
          cost: parseFloat(form.cost) || 0,
          report_date: form.report_date || null,
          estimated_done: form.estimated_done || null,
          completed_at: form.status === 'completed' ? (rec.completed_at || new Date().toISOString()) : null,
          completed: form.status === 'completed',
          updated_at: new Date().toISOString(),
        }
        await supabase.from('maintenance').update(payload).eq('id', rec.id)
        await addLog('更新維修紀錄', `機台 ${form.machine_name} → ${statusInfo(form.status).label}`)
        showToast('✓ 已更新')
      }
      setActionModal(null); load()
    } catch (e) {
      showToast('操作失敗：' + e.message, 'error')
    } finally { setSaving(false) }
  }

  // ── Delete ──
  const handleDelete = async (id, name) => {
    if (!window.confirm(`確定刪除「${name}」的維修紀錄？`)) return
    const { error } = await supabase.from('maintenance').delete().eq('id', id)
    if (error) { showToast('刪除失敗', 'error'); return }
    await addLog('刪除維修紀錄', `刪除機台 ${name} 紀錄`)
    showToast('✓ 已刪除'); load()
  }

  // ── Machine CRUD ──
  const handleSaveMachine = async () => {
    if (!machineForm.name || !machineForm.code) { showToast('請填寫機台名稱和代號', 'error'); return }
    setSaving(true)
    const { id, created_at, ...rest } = machineForm
    if (editingMachine) {
      const { error } = await supabase.from('machines').update(rest).eq('id', editingMachine.id)
      if (error) { showToast('更新失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 機台已更新'); setEditingMachine(null)
    } else {
      const { error } = await supabase.from('machines').insert({ ...rest, company_id: userProfile?.company_id || null })
      if (error) { showToast('新增失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 機台已新增'); setShowAddMachine(false)
    }
    setSaving(false); setMachineForm(emptyMachineForm()); load()
  }

  const handleDeleteMachine = async (id, name) => {
    if (!window.confirm(`確定刪除機台「${name}」？`)) return
    const { error } = await supabase.from('machines').delete().eq('id', id)
    if (error) { showToast('刪除失敗', 'error'); return }
    showToast('✓ 已刪除'); load()
  }

  // ── Render helpers ──
  const StatusTab = ({ value, label, count, warn }) => (
    <button
      style={{ ...st.btn(filterStatus === value ? (warn ? 'danger' : 'primary') : 'default'), padding: '7px 12px', fontSize: 12 }}
      onClick={() => { setFilterStatus(value); setPage(1) }}>
      {label}
      {count > 0 && <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4, background: filterStatus === value ? 'rgba(255,255,255,0.25)' : C.border, borderRadius: 10, padding: '1px 5px' }}>{count}</span>}
    </button>
  )

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader title="機械維修" en="Maintenance" vi="Bảo trì" />

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { icon: '🔴', label: _('mntKpiStopped'),  value: stats.stopped,    color: '#ef4444', bg: '#fee2e2', warn: stats.stopped > 0 },
          { icon: '⏳', label: '待處理',              value: stats.pending,    color: '#f59e0b', bg: '#fffbeb' },
          { icon: '⚙️', label: '維修中',              value: stats.inProgress, color: '#3b82f6', bg: '#eff6ff' },
          { icon: '📦', label: _('mntKpiWaitParts'), value: stats.waitParts,  color: '#7c3aed', bg: '#ede9fe' },
          { icon: '✅', label: '已完成',              value: stats.completed,  color: '#10b981', bg: '#d1fae5' },
          { icon: '⚙️', label: _('mntKpiTotal'),     value: machines.length,  color: C.accent,  bg: C.accentDim },
        ].map((k, i) => (
          <div key={i} style={{ background: k.bg, border: `2px solid ${k.color + (k.warn ? '' : '33')}`, borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 18 }}>{k.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: C.sub, fontWeight: 600, marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tab Nav */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['records', _('mntTabRecords')], ['machines', _('mntTabMachines')]].map(([key, label]) => (
          <button key={key} style={{ ...st.btn(activeTab === key ? 'primary' : 'default'), padding: '10px 20px' }}
            onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════ 維修紀錄 ══════════════ */}
      {activeTab === 'records' && (
        <>
          {/* Filter row */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <StatusTab value="active" label={_('mntFilterActive')} count={stats.pending + stats.inProgress + stats.waitParts} />
              <StatusTab value="all"    label={_('mntFilterAll')} count={records.length} />
              <StatusTab value="pending"       label="⏳ 待處理" count={stats.pending} warn />
              <StatusTab value="in_progress"   label="⚙️ 維修中" count={stats.inProgress} />
              <StatusTab value="waiting_parts" label={_('mntFilterWaitParts')} count={stats.waitParts} />
              <StatusTab value="completed"     label={_('mntFilterCompleted')} count={stats.completed} />
            </div>
            <button style={{ ...st.btn(), padding: '9px 16px' }}
              onClick={() => openAction('report')}>
              <span>{_('mntNewReport')}</span>
              <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>New Report</span>
            </button>
          </div>

          {/* Search + type filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <input
              placeholder={_('mntSearchPh')}
              value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
              style={{ ...st.input, flex: 1, minWidth: 200, maxWidth: 320 }}
            />
            <select style={{ ...st.input, width: 130 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="all">全部類型</option>
              {TYPE_OPTIONS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          {/* Record cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: C.muted }}>{_('loading')}</div>
          ) : paged.length === 0 ? (
            <EmptyState icon="🔧" text={search ? _('mntNoMatch') : filterStatus === 'active' ? _('mntNoActive') : _('mntNoRecords')} />
          ) : (
            <>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
                共 <strong style={{ color: C.text }}>{filtered.length}</strong> 筆
                {filterStatus === 'active' && <span style={{ marginLeft: 8, color: '#10b981' }}>（僅顯示進行中）</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {paged.map(r => (
                  <RecordCard
                    key={r.id} r={r} canManage={canManage} canAssign={canAssign}
                    onDetail={setDetail}
                    onAction={openAction}
                    onDelete={handleDelete}
                    typeInfo={typeInfo}
                    statusInfo={statusInfo}
                    _={_}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
                  <button disabled={page === 1} style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => p-1)}>{_('prevPage')}</button>
                  <span style={{ padding: '8px 14px', fontSize: 13, color: C.muted }}>{page} / {totalPages}</span>
                  <button disabled={page === totalPages} style={{ ...st.btn('default'), padding: '8px 14px' }} onClick={() => setPage(p => p+1)}>{_('nextPage')}</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ══════════════ 機台管理 ══════════════ */}
      {activeTab === 'machines' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <button style={{ ...st.btn(), padding: '9px 16px' }}
              onClick={() => { setShowAddMachine(!showAddMachine); setMachineForm(emptyMachineForm()); setEditingMachine(null) }}>
              <span>{showAddMachine ? '✕ 取消' : '+ 新增機台'}</span>
              <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Add Machine</span>
            </button>
          </div>

          {showAddMachine && !editingMachine && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>⚙️ 新增機台</div>
              <MachineFormBody form={machineForm} setForm={setMachineForm} />
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleSaveMachine} disabled={saving}>
                  {saving ? _('savingBtn') : '確認新增'}
                </button>
                <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setShowAddMachine(false)}>{_('cancel')}</button>
              </div>
            </div>
          )}

          {machines.length === 0 ? (
            <EmptyState icon="⚙️" text={_('mntNoMachines')} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {machines.map(m => {
                const sMap = { active: { label: '正常', color: '#10b981', bg: '#d1fae5' }, maintenance: { label: '維修中', color: '#f59e0b', bg: '#fffbeb' }, inactive: { label: '停用', color: '#94a3b8', bg: '#f1f5f9' } }
                const ms = sMap[m.status] || sMap.active
                const machRepairs = records.filter(r => r.machine_code === m.code)
                const activeRepairs = machRepairs.filter(r => r.status !== 'completed').length
                return (
                  <div key={m.id} style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${activeRepairs > 0 ? '#ef444455' : C.border}`, padding: '16px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{m.name}</div>
                        <div style={{ fontSize: 11, fontFamily: 'monospace', color: C.accent, marginTop: 2 }}>{m.code}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: ms.color, background: ms.bg, padding: '3px 10px', borderRadius: 20 }}>{ms.label}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                      {m.department && <div style={{ fontSize: 12, color: C.muted }}>🏭 {m.department}{m.location && ` · ${m.location}`}</div>}
                      {m.responsible && <div style={{ fontSize: 12, color: C.muted }}>👤 {m.responsible}</div>}
                      {m.supplier && <div style={{ fontSize: 12, color: C.muted }}>🏪 {m.supplier}</div>}
                      {m.note && <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic' }}>📝 {m.note}</div>}
                    </div>
                    {/* Repair count */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                      <div style={{ flex: 1, textAlign: 'center', background: activeRepairs > 0 ? '#fee2e2' : '#f8fafc', borderRadius: 8, padding: '6px' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: activeRepairs > 0 ? '#ef4444' : C.muted }}>{activeRepairs}</div>
                        <div style={{ fontSize: 9, color: C.muted }}>待處理</div>
                      </div>
                      <div style={{ flex: 1, textAlign: 'center', background: '#f8fafc', borderRadius: 8, padding: '6px' }}>
                        <div style={{ fontSize: 18, fontWeight: 900, color: C.muted }}>{machRepairs.length}</div>
                        <div style={{ fontSize: 9, color: C.muted }}>總紀錄</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={{ ...st.btn('warning'), flex: 1, padding: '7px' }} onClick={() => { setEditingMachine(m); setMachineForm({ ...m }) }}>編輯</button>
                      {canManage && <button style={{ ...st.btn('danger'), padding: '7px 10px' }} onClick={() => handleDeleteMachine(m.id, m.name)}>刪除</button>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* ── STEP 1：新增報修 Modal ── */}
      {actionModal?.type === 'report' && (
        <Modal title={_('mntReportTitle')} onClose={() => setActionModal(null)} wide>
          <div style={{ marginBottom: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 12, color: '#1d4ed8' }}>
            步驟 1 / 4　填寫問題描述 → 送出後由主管指派維修人員
          </div>
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '14px', border: `1.5px solid ${C.border}`, marginBottom: 14 }}>
            <label style={{ ...st.label, marginBottom: 8, display: 'block' }}>{_('mntSelectMachine')}</label>
            <SearchSelect options={machineOptions} value={machines.find(m => m.code === form.machine_code)?.id || ''} onChange={handleMachineSelect} placeholder={_('mntSearchMachine')} />
            {form.machine_code && (
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: C.accent, fontWeight: 700, background: C.accentDim, padding: '4px 10px', borderRadius: 16 }}>⚙️ {form.machine_code}</span>
                {form.machine_name && <span style={{ fontSize: 12, color: C.sub, background: '#f1f5f9', padding: '4px 10px', borderRadius: 16 }}>{form.machine_name}</span>}
              </div>
            )}
            {!form.machine_code && (
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div><label style={st.label}>機台名稱（手動輸入）</label><input style={st.input} value={form.machine_name} placeholder="機台名稱" onChange={e => setForm(f => ({ ...f, machine_name: e.target.value }))} /></div>
                <div><label style={st.label}>機台代號</label><input style={st.input} value={form.machine_code} placeholder="代號" onChange={e => setForm(f => ({ ...f, machine_code: e.target.value }))} /></div>
              </div>
            )}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ ...st.label, marginBottom: 8, display: 'block' }}>{_('mntReportType')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8 }}>
              {TYPE_OPTIONS.map(t => (
                <button key={t.key} type="button" onClick={() => setForm(f => ({ ...f, type: t.key }))}
                  style={{ padding: '10px 6px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${form.type === t.key ? t.color : C.border}`, background: form.type === t.key ? t.bg : '#fff', color: form.type === t.key ? t.color : C.muted, fontWeight: 700, fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <span style={{ fontSize: 18 }}>{t.icon}</span><span>{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, cursor: 'pointer', background: form.is_stopped ? '#fee2e2' : '#f8fafc', border: `2px solid ${form.is_stopped ? '#ef4444' : C.border}`, marginBottom: 14 }}>
            <input type="checkbox" checked={form.is_stopped} onChange={e => setForm(f => ({ ...f, is_stopped: e.target.checked }))} style={{ width: 22, height: 22 }} />
            <div><div style={{ fontSize: 15, fontWeight: 800, color: form.is_stopped ? '#ef4444' : C.muted }}>{_('mntMachineDown')}</div><div style={{ fontSize: 11, color: C.muted }}>勾選後將優先處理</div></div>
          </label>
          <div style={{ marginBottom: 14 }}>
            <label style={st.label}>{_('mntProblemDesc')}</label>
            <textarea style={{ ...st.input, minHeight: 80, resize: 'vertical' }} value={form.description} placeholder={_('mntProblemPh')} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>{_('mntReporterName')}</label>
            <input style={st.input} value={form.reporter} placeholder="填寫您的姓名" onChange={e => setForm(f => ({ ...f, reporter: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '13px' }} onClick={handleSaveReport} disabled={saving}>{saving ? '送出中...' : _('mntSubmitReport')}</button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => setActionModal(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── STEP 2：指派並開始維修 Modal ── */}
      {actionModal?.type === 'assign' && (
        <Modal title={`${_('mntAssignTitle')} — ${actionModal.record.machine_name}`} onClose={() => setActionModal(null)}>
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 12, color: '#1d4ed8' }}>
            步驟 2 / 4　確認誰來處理，設定預計完成時間
          </div>
          <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, marginBottom: 16, border: `1.5px solid ${C.border}` }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{actionModal.record.machine_name}</span>
              <Badge label={`${typeInfo(actionModal.record.type).icon} ${typeInfo(actionModal.record.type).label}`} color={typeInfo(actionModal.record.type).color} bg={typeInfo(actionModal.record.type).bg} />
            </div>
            {actionModal.record.description && <div style={{ fontSize: 12, color: C.text, padding: '6px 8px', background: '#fff5f5', borderRadius: 8, border: '1px solid #fca5a5' }}>{actionModal.record.description}</div>}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={st.label}>{_('mntAssignee')}</label>
            <input style={st.input} value={form.assignee || ''} placeholder="負責維修的人員姓名" onChange={e => setForm(f => ({ ...f, assignee: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>{_('mntEstDone')}</label>
            <input type="date" style={st.input} value={form.estimated_done || ''} onChange={e => setForm(f => ({ ...f, estimated_done: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn('primary'), flex: 1, padding: '13px' }} onClick={() => handleActionSave('assign')} disabled={saving}>{saving ? '處理中...' : _('mntConfirmAssign')}</button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => setActionModal(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── STEP 3：等待零件 Modal ── */}
      {actionModal?.type === 'waitparts' && (
        <Modal title={`${_('mntWaitPartsTitle')} — ${actionModal.record.machine_name}`} onClose={() => setActionModal(null)}>
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fef9c3', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
            步驟 3a / 4　記錄需要哪些零件，等到貨後再繼續
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>{_('mntNeededParts')}</label>
            <input style={st.input} value={form.parts_used || ''} placeholder={_('mntNeededPartsPh')} onChange={e => setForm(f => ({ ...f, parts_used: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn('warning'), flex: 1, padding: '13px' }} onClick={() => handleActionSave('waitparts')} disabled={saving}>{saving ? '更新中...' : _('mntConfirmWaitParts')}</button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => setActionModal(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── STEP 4：回報完工 Modal ── */}
      {actionModal?.type === 'complete' && (
        <Modal title={`${_('mntCompleteTitle')} — ${actionModal.record.machine_name}`} onClose={() => setActionModal(null)}>
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#d1fae5', borderRadius: 8, fontSize: 12, color: '#065f46' }}>
            步驟 4 / 4　填寫如何解決，完成此維修工單
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={st.label}>{_('mntSolution')}</label>
            <textarea style={{ ...st.input, minHeight: 90, resize: 'vertical' }} value={form.solution || ''} placeholder={_('mntSolutionPh')} onChange={e => setForm(f => ({ ...f, solution: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={st.label}>{_('mntPartsUsed')}</label>
            <input style={st.input} value={form.parts_used || ''} placeholder="例：軸承 × 2、油封 × 1..." onChange={e => setForm(f => ({ ...f, parts_used: e.target.value }))} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={st.label}>{_('mntRepairCost')}</label>
            <input type="number" min="0" style={st.input} value={form.cost || ''} placeholder="0（無費用可留空）" onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn('success'), flex: 1, padding: '13px', fontSize: 15 }} onClick={() => handleActionSave('complete')} disabled={saving}>{saving ? _('savingBtn') : _('mntConfirmComplete')}</button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => setActionModal(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── 全欄位修改 Modal（canManage 限定）── */}
      {actionModal?.type === 'edit' && (
        <Modal title={`✏️ 修改紀錄 — ${actionModal.record.machine_name}`} onClose={() => setActionModal(null)} wide>
          <label style={{ ...st.label, marginBottom: 8, display: 'block' }}>處理狀態</label>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 8, marginBottom: 14 }}>
            {STATUS_OPTIONS.map(s => (
              <button key={s.key} type="button" onClick={() => setForm(f => ({ ...f, status: s.key }))}
                style={{ padding: '12px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${form.status === s.key ? s.color : C.border}`, background: form.status === s.key ? s.bg : '#fff', color: form.status === s.key ? s.color : C.muted, fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <span style={{ fontSize: 18 }}>{s.icon}</span> {s.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={st.label}>維修負責人</label><input style={st.input} value={form.assignee || ''} placeholder="維修人員姓名" onChange={e => setForm(f => ({ ...f, assignee: e.target.value }))} /></div>
            <div><label style={st.label}>費用 NT$</label><input type="number" min="0" style={st.input} value={form.cost || ''} placeholder="0" onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} /></div>
            <div><label style={st.label}>預計完成日</label><input type="date" style={st.input} value={form.estimated_done || ''} onChange={e => setForm(f => ({ ...f, estimated_done: e.target.value }))} /></div>
          </div>
          <div style={{ marginBottom: 12 }}><label style={st.label}>{_('mntPartsUsed')}</label><input style={st.input} value={form.parts_used || ''} placeholder="例：軸承 × 2..." onChange={e => setForm(f => ({ ...f, parts_used: e.target.value }))} /></div>
          <div style={{ marginBottom: 14 }}><label style={st.label}>解決方案 / 維修方式</label><textarea style={{ ...st.input, minHeight: 80, resize: 'vertical' }} value={form.solution || ''} placeholder="填寫維修方式..." onChange={e => setForm(f => ({ ...f, solution: e.target.value }))} /></div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            {[{ key: 'is_stopped', label: '🔴 機台停機' }, { key: 'reported', label: '📢 已通報主管' }].map(tog => (
              <label key={tog.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, cursor: 'pointer', background: form[tog.key] ? (tog.key === 'is_stopped' ? '#fee2e2' : C.accentDim) : '#f8fafc', border: `1.5px solid ${form[tog.key] ? (tog.key === 'is_stopped' ? '#ef4444' : C.accent) : C.border}`, flex: '1 1 140px' }}>
                <input type="checkbox" checked={!!form[tog.key]} onChange={e => setForm(f => ({ ...f, [tog.key]: e.target.checked }))} style={{ width: 18, height: 18 }} />
                <span style={{ fontSize: 13, fontWeight: 700 }}>{tog.label}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...st.btn(form.status === 'completed' ? 'success' : ''), flex: 1, padding: '13px' }} onClick={() => handleActionSave('edit')} disabled={saving}>{saving ? _('savingBtn') : form.status === 'completed' ? '✓ 確認完工' : '✓ 儲存變更'}</button>
            <button style={{ ...st.btn('default'), padding: '13px 18px' }} onClick={() => setActionModal(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── Machine edit modal ── */}
      {editingMachine && (
        <Modal title={`✏️ 編輯機台 — ${editingMachine.name}`} onClose={() => setEditingMachine(null)}>
          <MachineFormBody form={machineForm} setForm={setMachineForm} />
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '12px' }} onClick={handleSaveMachine} disabled={saving}>
              {saving ? _('savingBtn') : '確認更新'}
            </button>
            <button style={{ ...st.btn('default'), padding: '12px 18px' }} onClick={() => setEditingMachine(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── Detail modal ── */}
      {detail && (() => {
        const { solution, parts } = parseSolution(detail.solution)
        return (
          <Modal title={`${_('mntDetailTitle')} — ${detail.machine_name}`} onClose={() => setDetail(null)} wide>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10, marginBottom: 14 }}>
              {[
                ['機台名稱', detail.machine_name],
                [_('mntMachineCode'), detail.machine_code || '-'],
                [_('mntReportDate'), formatDate(detail.report_date)],
                ['預計完成', detail.estimated_done || '-'],
                ['通報人',   detail.reporter || '-'],
                ['維修負責人', detail.assignee || '-'],
                ['機器負責人', detail.machine_responsible || '-'],
                ['維修費用', detail.cost > 0 ? `NT$ ${Number(detail.cost).toLocaleString()}` : '-'],
                ['完成時間', detail.completed_at ? formatDate(detail.completed_at) : '-'],
              ].map(([label, value]) => (
                <div key={label} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 14, color: C.text, fontWeight: 700 }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Badge label={`${typeInfo(detail.type).icon} ${typeInfo(detail.type).label}`} color={typeInfo(detail.type).color} bg={typeInfo(detail.type).bg} />
              <Badge label={`${statusInfo(detail.status).icon} ${statusInfo(detail.status).label}`} color={statusInfo(detail.status).color} bg={statusInfo(detail.status).bg} />
              {detail.is_stopped && <Badge label="🔴 停機中" color="#ef4444" bg="#fee2e2" />}
              {detail.reported && <Badge label="📢 已通報主管" color={C.accent} bg={C.accentDim} />}
            </div>
            {detail.description && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6 }}>問題描述</div>
                <div style={{ padding: '12px', background: '#fff5f5', border: `1.5px solid #fca5a5`, borderRadius: 10, fontSize: 14, lineHeight: 1.7 }}>{detail.description}</div>
              </div>
            )}
            {parts && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6 }}>🔩 使用備品/更換零件</div>
                <div style={{ padding: '12px', background: '#fffbeb', border: `1.5px solid #fde68a`, borderRadius: 10, fontSize: 14 }}>{parts}</div>
              </div>
            )}
            {solution && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6 }}>解決方案 / 維修方式</div>
                <div style={{ padding: '12px', background: '#d1fae5', border: `1.5px solid #a7f3d0`, borderRadius: 10, fontSize: 14, lineHeight: 1.7 }}>{solution}</div>
              </div>
            )}
          </Modal>
        )
      })()}
    </div>
  )
}
